import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { WebhookData, WebhookStatus } from './types';
import { nanoid } from 'nanoid';
import { useTrackingData } from './useTrackingData';
import { createTracking, deleteTracking } from '@/services/trackingService';
import { useAuth } from '@/hooks/useAuth';
import { useTrackingStore } from '@/store/trackingStore';
import { useQueryClient } from '@tanstack/react-query';

export function useWebhooks(
  onTabChange?: () => void,
  savedWebhooks: WebhookData[] = [],
  onWebhooksSaved?: (webhooks: WebhookData[]) => void
) {
  useLanguage();
  const { data: trackingData } = useTrackingData();
  const { user, token } = useAuth();
  const { selectedPlatforms, pendingNewPlatforms, clearPendingPlatforms } = useTrackingStore();
  const queryClient = useQueryClient();
  const [webhooks, setWebhooks] = useState<WebhookData[]>(savedWebhooks);
  const [selectedWebhooks, setSelectedWebhooks] = useState<string[]>([]);
  const initializedRef = useRef(false);

  // Criar webhooks baseado nas plataformas selecionadas no store
  useEffect(() => {
    if (selectedPlatforms.length > 0) {
      // Criar webhooks apenas para plataformas que ainda não têm webhooks
      const newWebhooks = selectedPlatforms
        .filter(platform => !webhooks.some(w => w.platform === platform.slug))
        .map(platform => ({
          id: nanoid(),
          platform: platform.slug,
          platformId: platform.platformId,
          url: `https://api.hostvsl.com/webhook/${platform.slug}/${Math.random().toString(36).substring(2, 15)}`,
          status: 'creation' as WebhookStatus,
          selectedVideoId: '',
        }));

      if (newWebhooks.length > 0) {
        const combined = [...webhooks, ...newWebhooks];
        setWebhooks(combined);

        // Auto-save new webhooks to persist them as drafts
        if (onWebhooksSaved) {
          setTimeout(() => {
            onWebhooksSaved(combined);
          }, 0);
        }
      }
    }
  }, [selectedPlatforms]);

  // Criar webhooks para plataformas já existentes que o usuário quer usar novamente
  useEffect(() => {
    if (pendingNewPlatforms.length > 0) {
      const newWebhooks = pendingNewPlatforms.map(platform => ({
        id: nanoid(),
        platform: platform.slug,
        platformId: platform.platformId,
        url: '',
        status: 'creation' as WebhookStatus,
        selectedVideoId: '',
      }));

      const combined = [...webhooks, ...newWebhooks];
      setWebhooks(combined);
      clearPendingPlatforms();

      if (onWebhooksSaved) {
        setTimeout(() => onWebhooksSaved(combined), 0);
      }
    }
  }, [pendingNewPlatforms]);

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast(translateInterfaceText("URL copiada para a área de transferência"), {
      position: "top-center",
    });
  };

  const handleDeleteWebhook = async (id: string) => {
    const webhook = webhooks.find(w => w.id === id);

    if (!webhook) return;

    // Se o webhook tem integrationId, deletar no backend
    if (webhook.integrationId && token) {
      try {
        await deleteTracking(String(webhook.integrationId), token);
      } catch (error: any) {
        console.error('Erro ao excluir tracking:', error);
        const errorMessage = error.response?.data?.message || translateInterfaceText("Erro ao excluir tracking. Tente novamente.");
        toast.error(errorMessage, {
          position: "top-center",
        });
        return; // Não remove da lista se falhar
      }
    }

    // Remover da lista local
    const updatedWebhooks = webhooks.filter(webhook => webhook.id !== id);
    setWebhooks(updatedWebhooks);

    // Remover plataforma do store se não houver mais webhooks para ela
    const hasOtherWebhooksForPlatform = updatedWebhooks.some(w => w.platform === webhook.platform);
    if (!hasOtherWebhooksForPlatform) {
      const trackingStore = useTrackingStore.getState();
      trackingStore.removePlatform(webhook.platform);
    }

    // Update saved webhooks in parent
    if (onWebhooksSaved) {
      onWebhooksSaved(updatedWebhooks);
    }

    // Invalidar cache do React Query para atualizar os dados
    queryClient.invalidateQueries({ queryKey: ['tracking-data'] });

    toast.success(translateInterfaceText("Tracking excluído com sucesso!"), {
      position: "top-center",
    });
  };

  const handleTokenChange = (id: string, token: string) => {
    setWebhooks(webhooks.map(webhook =>
      webhook.id === id
        ? { ...webhook, platformToken: token }
        : webhook
    ));
  };

  const handleVideoChange = (id: string, videoId: string) => {
    // Check if this video is already assigned to another webhook of the same platform
    const currentWebhook = webhooks.find(w => w.id === id);
    if (!currentWebhook) return;
    
    const isDuplicate = webhooks.some(webhook => 
      webhook.id !== id && 
      webhook.platform === currentWebhook.platform && 
      webhook.selectedVideoId === videoId
    );
    
    if (isDuplicate) {
      toast.error(translateInterfaceText("Este vídeo já está sendo usado nesta plataforma."), {
        position: "top-center",
        description: translateInterfaceText("Selecione um vídeo diferente."),
      });
      return;
    }
    
    setWebhooks(webhooks.map(webhook => 
      webhook.id === id 
        ? { ...webhook, selectedVideoId: videoId }
        : webhook
    ));
  };

  const handleSaveTracking = async (id: string) => {
    const webhook = webhooks.find(w => w.id === id);

    if (!webhook) return;

    if (!webhook.selectedVideoId) {
      toast.error(translateInterfaceText("Selecione um vídeo para continuar."), {
        position: "top-center",
      });
      return;
    }

    if (webhook.platform === 'payt' && !webhook.platformToken?.trim()) {
      toast.error(translateInterfaceText("Informe a integration_key da Payt."), { position: 'top-center' });
      return;
    }

    if (!user || !token) {
      toast.error(translateInterfaceText("Usuário não autenticado"), {
        position: "top-center",
      });
      return;
    }

    try {
      // Usar o platformId que já está armazenado no webhook
      if (!webhook.platformId) {
        toast.error(translateInterfaceText("ID da plataforma não encontrado"), {
          position: "top-center",
        });
        return;
      }

      // Chamar API para criar tracking
      const response = await createTracking(
        {
          userId: user.id_user,
          plataformId: webhook.platformId,
          videoId: webhook.selectedVideoId,
          plataformToken: webhook.platformToken,
        },
        token
      );

      // Atualizar webhook com a URL retornada e status
      const updatedWebhooks = webhooks.map(w =>
        w.id === id
          ? {
              ...w,
              url: response.webhookUrl,
              integrationId: response.id,
              status: 'waiting' as WebhookStatus
            }
          : w
      );
      setWebhooks(updatedWebhooks);

      // Save the updated webhooks
      if (onWebhooksSaved) {
        onWebhooksSaved(updatedWebhooks);
      }

      // Invalidar cache do React Query para atualizar os dados
      queryClient.invalidateQueries({ queryKey: ['tracking-data'] });

      toast.success(translateInterfaceText("Tracking salvo com sucesso!"), {
        position: "top-center",
      });
    } catch (error: any) {
      console.error('Erro ao salvar tracking:', error);

      const errorMessage = error.response?.data?.message || translateInterfaceText("Erro ao salvar tracking. Tente novamente.");

      toast.error(errorMessage, {
        position: "top-center",
      });
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelectedWebhooks(prev => 
      prev.includes(id) 
        ? prev.filter(p => p !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedWebhooks.length === webhooks.length) {
      setSelectedWebhooks([]);
    } else {
      setSelectedWebhooks(webhooks.map(w => w.id));
    }
  };

  const handleClearSelection = () => {
    setSelectedWebhooks([]);
  };

  const handleDeleteSelected = async () => {
    const count = selectedWebhooks.length;
    const webhooksToDelete = webhooks.filter(w => selectedWebhooks.includes(w.id));

    // Deletar no backend
    if (token) {
      const deletePromises = webhooksToDelete
        .filter(w => w.integrationId) // Apenas os que têm integrationId
        .map(w => deleteTracking(String(w.integrationId), token).catch(error => {
          console.error(`Erro ao excluir tracking ${w.id}:`, error);
          throw error;
        }));

      try {
        await Promise.all(deletePromises);
      } catch (error: any) {
        console.error('Erro ao excluir trackings:', error);
        const errorMessage = error.response?.data?.message || translateInterfaceText("Erro ao excluir alguns trackings.");
        toast.error(errorMessage, {
          position: "top-center",
        });
        return; // Não remove da lista se falhar
      }
    }

    // Remover da lista local
    const updatedWebhooks = webhooks.filter(w => !selectedWebhooks.includes(w.id));
    setWebhooks(updatedWebhooks);
    setSelectedWebhooks([]);

    // Remover plataformas do store que não têm mais webhooks
    const trackingStore = useTrackingStore.getState();
    const deletedPlatforms = webhooksToDelete.map(w => w.platform);
    const uniqueDeletedPlatforms = Array.from(new Set(deletedPlatforms));

    uniqueDeletedPlatforms.forEach(platform => {
      const hasRemainingWebhooks = updatedWebhooks.some(w => w.platform === platform);
      if (!hasRemainingWebhooks) {
        trackingStore.removePlatform(platform);
      }
    });

    if (onWebhooksSaved) {
      onWebhooksSaved(updatedWebhooks);
    }

    // Invalidar cache do React Query para atualizar os dados
    queryClient.invalidateQueries({ queryKey: ['tracking-data'] });

    toast.success(translateInterfaceText("{value0} tracking(s) excluído(s) com sucesso!", { value0: (count) }), {
      position: "top-center",
    });
  };

  // Available videos for selection - usar dados da API ou array vazio
  const videos = trackingData?.videos?.map(video => ({
    id: video.id,
    title: video.name,
  })) || [];

  return {
    webhooks,
    videos,
    selectedWebhooks,
    handleCopyUrl,
    handleDeleteWebhook,
    handleTokenChange,
    handleVideoChange,
    handleSaveTracking,
    handleToggleSelect,
    handleSelectAll,
    handleClearSelection,
    handleDeleteSelected,
  };
}
